import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import routes from "../../../../routes/routes";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { setUseCase, setAvailabilities } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { availabilitiesApiSlice } from "../../../../api/availabilities/availabilitiesApiSlice";

import { KeyHandoverServiceProvider } from "../context/KeyHandoverServiceContext";
import { KeyHandoverScheduleProvider } from "../context/KeyHandoverScheduleContext";
import KeyHandoverScheduleStep from "../steps/KeyHandoverScheduleStep";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";

import { parseBookingQuery } from "../common/url/bookingQuery";
import { useScheduleHydration } from "../hooks/useScheduleHydration";
import { useScheduleUrlSync } from "../hooks/useScheduleUrlSync";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../components/ui/spinner";
import { useBookingWindow } from "../../common/hooks/useBookingWindow";

/**
 * KeyHandoverSchedulePage Component
 * ---------------------------------------------------------------
 * Manages the schedule selection step in the key handover booking flow.
 *
 * @remarks
 * This page handles:
 * - Location and service hydration from URL
 * - Schedule (pickupDate, pickupTime, pickupDays) hydration and URL synchronization
 * - Availability fetching for the selected service
 * - Navigation guards to ensure prerequisite data exists
 * - Rendering the schedule selection UI
 *
 * @example
 * Route: /key-handover/schedule?locationId=loc-123&serviceId=svc-456&pickupDate=2025-12-25&pickupTime=14:00&pickupDays=3
 */
const KeyHandoverSchedulePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux selectors
  const selectedGroup = useSelector((state: RootState) => state.bookingCreation.selectedGroup);
  const selectedServiceType = useSelector(
    (state: RootState) => state.bookingCreation.keyHandoverSelectedService
  );
  const assetGroupId = useSelector((state: RootState) => state.bookingCreation.assetGroupId);
  const availabilities = useSelector((state: RootState) => state.bookingCreation.availabilities);
  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);

  // Derived state
  const hasAvailabilities = Boolean(
    availabilities && typeof availabilities === "object" && Object.keys(availabilities).length > 0
  );
  
  // Validate schedule: check if date, time, and days are all set
  // Use actual data properties (time, numberOfDays) rather than hasSelectedPickupTime flag
  // This ensures validation passes even when defaults are applied programmatically
  const scheduleValid = Boolean(
    orderData.checkInDate && 
    orderData.checkOutDate && 
    orderData.time && 
    typeof orderData.numberOfDays === 'number' && 
    orderData.numberOfDays > 0
  );

  // One-time initialization
  useEffect(() => {
    dispatch(setUseCase({ useCase: "keys" }));
  }, [dispatch]);

  // Navigation guards (ensure prerequisite data exists)
  useEffect(() => {
    const query = parseBookingQuery(location.search);

    if (!query.locationId) {
      navigate(
        { pathname: routes.keyHandoverLocationSelectionPath, search: location.search },
        { replace: true }
      );
      return;
    }

    if (!query.serviceId) {
      navigate(
        { pathname: routes.keyHandoverServiceSelectionPath, search: location.search },
        { replace: true }
      );
    }
  }, [location.search, navigate]);

  // The configured booking horizon for this use case — replaces the hardcoded
  // `add(1, "months").endOf("month")` this file computed inline. One month is
  // the DEFAULT here, so an unconfigured tenant requests exactly the same range
  // as before. `earliest` carries the lead buffer, which defaults to 0 for this
  // use case (the customer may book now), and is the same rule the app API
  // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
  const bookingWindow = useBookingWindow("keys");

  // Fetch availabilities when service is selected
  useEffect(() => {
    const effectiveAssetGroupId = assetGroupId || selectedGroup?.id;
    if (!effectiveAssetGroupId) return;
    if (availabilities) return; // Already loaded

    const fromDate = bookingWindow.earliest.toDate();
    const toDate = bookingWindow.latest.toDate();

    // Get customerAgreementId from URL if present
    let customerAgreementId: string | undefined = undefined;
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const id = params.get("customerAgreementId");
      if (id) customerAgreementId = id;
    }

    dispatch(
      availabilitiesApiSlice.endpoints.fetchAssetGroupAvailabilities.initiate({
        assetGroupId: effectiveAssetGroupId,
        from: fromDate,
        to: toDate,
        ...(customerAgreementId ? { customerAgreementId } : {}),
      })
    )
      .unwrap()
      .then((data) => {
        if (data?.availabilities) {
          dispatch(setAvailabilities(data.availabilities));
        } else {
          // Set empty object to stop loading spinner
          dispatch(setAvailabilities({} as any));
        }
      })
      .catch(() => {
        // On error, stop spinner with empty availabilities
        dispatch(setAvailabilities({} as any));
      });
  }, [assetGroupId, selectedGroup?.id, availabilities, dispatch]);

  // Determine next route based on customerAgreementId in URL
  const params = new URLSearchParams(location.search);
  const customerAgreementId = params.get("customerAgreementId");
  let nextRoute: string;
  if (selectedServiceType === "Key-Pickup-And-Return") {
    nextRoute = routes.keyHandoverReturnSchedulePath;
  } else if (customerAgreementId) {
    nextRoute = routes.keyHandoverGuestDetailsPath;
  } else {
    nextRoute = routes.keyHandoverHostDetailsPath;
  }

  // Bottom actions for schedule page
  const bottomActions = Boolean(selectedGroup) && hasAvailabilities && (
    <KeyHandoverNavigateNextAction
      to={nextRoute}
      enabled={scheduleValid}
      bounceKey={scheduleValid ? selectedGroup?.id ?? null : null}
    />
  );

  return (
    <KeyHandoverPageLayout
      headerText={t("book_location.keys.order_data.header_text", {
        defaultValue: t("book_location.order_data.header_text"),
      })}
      backTo={routes.keyHandoverServiceSelectionPath}
      bottomActions={bottomActions}
    >
      <KeyHandoverContentWrapper>
        <SchedulePageContent />
      </KeyHandoverContentWrapper>
    </KeyHandoverPageLayout>
  );
};

/**
 * Inner component that handles hydration and rendering within provider scope.
 *
 * @remarks
 * Separated to allow hooks to be called within the provider context.
 * Must be wrapped in KeyHandoverProvider to access location context.
 * Service hydration happens within KeyHandoverServiceProvider.
 */
const SchedulePageContent: React.FC = () => {
  const scheduleHydratedRef = React.useRef(false);

  // Hydrate location from URL (requires KeyHandoverProvider context)
  const locationHydrated = useLocationHydration();

  // Hydrate schedule from URL (independent of providers)
  useScheduleHydration(locationHydrated);

  // Sync schedule changes back to URL
  useScheduleUrlSync(scheduleHydratedRef);

  // Mark schedule as hydrated after first render
  React.useEffect(() => {
    if (!scheduleHydratedRef.current) {
      scheduleHydratedRef.current = true;
    }
  }, []);

  if (!locationHydrated) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <KeyHandoverServiceProvider typesFilter="keys">
      <ServiceHydrationWrapper />
    </KeyHandoverServiceProvider>
  );
};

/**
 * Wrapper component to handle service hydration within ServiceProvider context.
 *
 * @remarks
 * Separated because useServiceHydration requires KeyHandoverServiceProvider context.
 * This pattern ensures proper hook execution within the correct provider hierarchy.
 */
const ServiceHydrationWrapper: React.FC = () => {
  // Hydrate service from URL and sync to URL (requires KeyHandoverServiceProvider context)
  useServiceHydration();

  return (
    <KeyHandoverScheduleProvider>
      <KeyHandoverScheduleStep />
    </KeyHandoverScheduleProvider>
  );
};

export default KeyHandoverSchedulePage;